import { useState } from "react";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import type { PoolPlayerPublic, SportPositions } from "@dugads/shared";
import { playerDisplayName, isReservePosition } from "@dugads/shared";
import { Search, GripVertical, CheckSquare, Square } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

type FilterMode = "all" | "available" | "assigned";

export interface PlayerAssignment {
  teamName: string;
  teamId: string;
  jerseyNumber?: number;
}

interface PlayerPoolSidebarProps {
  pool: PoolPlayerPublic[];
  /** Map of player_id → assignment info(s) for players assigned to teams in this age group */
  assignedMap: Map<string, PlayerAssignment[]>;
  /** Check if a player is available for the currently active team */
  isPlayerAvailable: (playerId: string) => boolean;
  /** Currently active team name -- to grey out players already on this team */
  activeTeamName?: string;
  /** Active team ID */
  activeTeamId?: string;
  /** Sport positions for determining reserve status */
  sportPositions?: SportPositions;
  /** Currently selected player for position assignment */
  selectedPlayerId: string | null;
  /** Multi-selected player IDs for bulk assignment */
  selectedPlayerIds?: Set<string>;
  saving: boolean;
  onSelectPlayer: (playerId: string | null) => void;
  onToggleMultiSelect?: (playerId: string) => void;
  onBulkAssign?: () => void;
  onClearSelection?: () => void;
}

// 184: one pool player. Extracted so it can own a dnd-kit useDraggable hook
// (hooks can't run inside the .map). PointerSensor's activation distance lets a
// plain tap still fire onClick (select / multi-select); only a real drag picks
// the player up. `disabled` mirrors the old `draggable` predicate.
function PoolPlayerItem({
  player,
  available,
  isOnCurrentTeam,
  isUnavailable,
  isSelected,
  isMultiSelected,
  multiSelectMode,
  saving,
  reserveBadges,
  unavailableTeamName,
  onClick,
}: {
  player: PoolPlayerPublic;
  available: boolean;
  isOnCurrentTeam: boolean;
  isUnavailable: boolean;
  isSelected: boolean;
  isMultiSelected: boolean;
  multiSelectMode: boolean;
  saving: boolean;
  reserveBadges: { teamId: string; teamName: string }[];
  unavailableTeamName: string | null;
  onClick: () => void;
}) {
  const name = playerDisplayName(player.first_name, player.surname);
  const drag = useDraggable({
    id: `pool-player-${player.id}`,
    data: { kind: "pool-player", playerId: player.id, label: name },
    disabled: !available || saving || multiSelectMode,
  });
  return (
    <button
      ref={drag.setNodeRef}
      {...drag.listeners}
      {...drag.attributes}
      type="button"
      onClick={onClick}
      disabled={saving}
      className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
        isMultiSelected
          ? "bg-primary/15 ring-1 ring-primary/30"
          : isSelected
            ? "bg-primary/15 ring-2 ring-primary/30 border border-primary"
            : isOnCurrentTeam
              ? "opacity-30 cursor-default"
              : isUnavailable
                ? "opacity-50 cursor-default"
                : "hover:bg-muted/50 cursor-pointer"
      } ${drag.isDragging ? "opacity-40" : ""}`}
    >
      {multiSelectMode && available ? (
        isMultiSelected ? (
          <CheckSquare className="h-3.5 w-3.5 text-primary shrink-0" />
        ) : (
          <Square className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        )
      ) : available ? (
        <GripVertical className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      ) : null}
      <span className={`truncate flex-1 ${isUnavailable && !multiSelectMode ? "ml-5" : ""}`}>
        {name}
      </span>
      {/* Show "Res [Team]" badges for reserves on higher-ranked teams */}
      {reserveBadges.map((a) => (
        <span
          key={a.teamId}
          className="rounded bg-orange-500/80 px-1 py-px text-[9px] font-bold text-white shrink-0"
        >
          Res {a.teamName}
        </span>
      ))}
      {/* Show team badge for unavailable players (starters or on current team) */}
      {isUnavailable && unavailableTeamName && (
        <Badge
          variant={isOnCurrentTeam ? "default" : "secondary"}
          className={`text-[10px] px-1 py-0 shrink-0 ${isOnCurrentTeam ? "bg-primary/50" : ""}`}
        >
          {unavailableTeamName}
        </Badge>
      )}
    </button>
  );
}

export function PlayerPoolSidebar({
  pool,
  assignedMap,
  isPlayerAvailable,
  activeTeamId,
  sportPositions,
  selectedPlayerId,
  selectedPlayerIds,
  saving,
  onSelectPlayer,
  onToggleMultiSelect,
  onBulkAssign,
  onClearSelection,
}: PlayerPoolSidebarProps) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterMode>("all");
  const [multiSelectMode, setMultiSelectMode] = useState(false);

  // 184: the pool is a drop target -- dragging an assigned player here removes
  // them from the team (handled in the parent's onDragEnd, which checks the
  // dragged player came from a position).
  const drop = useDroppable({ id: "pool-droppable", data: { kind: "pool" } });

  const filtered = pool.filter((p) => {
    const name = playerDisplayName(p.first_name, p.surname).toLowerCase();
    if (search && !name.includes(search.toLowerCase())) return false;
    if (filter === "available" && !isPlayerAvailable(p.id)) return false;
    if (filter === "assigned" && isPlayerAvailable(p.id) && !(assignedMap.get(p.id)?.length)) return false;
    return true;
  });

  const availableCount = pool.filter((p) => isPlayerAvailable(p.id)).length;
  const assignedCount = pool.length - availableCount;
  const multiCount = selectedPlayerIds?.size ?? 0;

  function handleToggleMultiSelectMode() {
    setMultiSelectMode((v) => !v);
    if (multiSelectMode) {
      onClearSelection?.();
    }
  }

  return (
    <div
      ref={drop.setNodeRef}
      className={`flex flex-col gap-2 w-full lg:w-64 lg:shrink-0 border-b pb-3 lg:border-b-0 lg:pb-0 lg:border-r lg:pr-3 transition-colors ${
        drop.isOver ? "border-destructive bg-destructive/5" : "border-border"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Player Pool ({pool.length})</span>
        {onToggleMultiSelect && (
          <Button
            variant={multiSelectMode ? "default" : "ghost"}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={handleToggleMultiSelectMode}
          >
            {multiSelectMode ? "Done" : "Select"}
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search players..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-md border border-input bg-background py-1.5 pl-8 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      {/* Filter pills */}
      <div className="flex gap-1">
        <Button
          variant={filter === "all" ? "default" : "outline"}
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => setFilter("all")}
        >
          All ({pool.length})
        </Button>
        <Button
          variant={filter === "available" ? "default" : "outline"}
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => setFilter("available")}
        >
          Available ({availableCount})
        </Button>
        <Button
          variant={filter === "assigned" ? "default" : "outline"}
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => setFilter("assigned")}
        >
          Assigned ({assignedCount})
        </Button>
      </div>

      {/* Bulk action bar */}
      {multiSelectMode && multiCount > 0 && onBulkAssign && (
        <div className="flex items-center gap-2 rounded-md bg-primary/10 px-2 py-1.5">
          <span className="text-xs font-medium flex-1">{multiCount} selected</span>
          <Button size="sm" className="h-6 px-2 text-xs" onClick={onBulkAssign} disabled={saving}>
            Assign All
          </Button>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onClearSelection}>
            Clear
          </Button>
        </div>
      )}

      {/* Player list -- capped shorter on mobile (pool stacks above the field, so
          keep the field reachable without scrolling past the whole pool) */}
      <div className="flex flex-col gap-0.5 overflow-y-auto max-h-[40vh] lg:max-h-[500px]">
        {filtered.map((p) => {
          const assignments = assignedMap.get(p.id) ?? [];
          const available = isPlayerAvailable(p.id);
          const isOnCurrentTeam = assignments.some((a) => a.teamId === activeTeamId);
          const isUnavailable = !available;
          const isSelected = p.id === selectedPlayerId;
          const isMultiSelected = selectedPlayerIds?.has(p.id) ?? false;
          // Reserve assignments on other teams (shown as badges when player is available)
          const reserveBadges = assignments
            .filter((a) => a.teamId !== activeTeamId && a.jerseyNumber && sportPositions && isReservePosition(sportPositions, a.jerseyNumber))
            .map((a) => ({ teamId: a.teamId, teamName: a.teamName }));
          return (
            <PoolPlayerItem
              key={p.id}
              player={p}
              available={available}
              isOnCurrentTeam={isOnCurrentTeam}
              isUnavailable={isUnavailable}
              isSelected={isSelected}
              isMultiSelected={isMultiSelected}
              multiSelectMode={multiSelectMode}
              saving={saving}
              reserveBadges={reserveBadges}
              unavailableTeamName={isUnavailable && assignments.length > 0 ? assignments[0].teamName : null}
              onClick={() => {
                if (saving) return;
                if (multiSelectMode && available && onToggleMultiSelect) {
                  onToggleMultiSelect(p.id);
                  return;
                }
                if (isUnavailable) return;
                onSelectPlayer(isSelected ? null : p.id);
              }}
            />
          );
        })}
        {filtered.length === 0 && (
          <p className="text-xs text-muted-foreground py-4 text-center">
            No players match
          </p>
        )}
      </div>
    </div>
  );
}
